import { ALL_VALUE } from "staticConfiguration";
import type { AutocompleteChangeReason } from "@mui/material";
import type { ScopedRoleSelect } from "core/components/ReactHookFormComponents/ReactHookScopedRoles/ReactHookScopedRoleSelector";
import type { SelectOption } from "core/entities";
import type { ScopedRoleEntry } from "core/entities/ScopeDefs";
import { isSelectOption } from "core/utils/isSelectOption";
import type { ScopeRoleSummaryDto } from "features/DMS/entities";
import { useMemo } from "react";
import {
  type FieldPath,
  type FieldValues,
  type UseFormReturn,
  useController,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { components } from "resources/api-schema";
import type { ScopedRoleTableRow } from "./ScopedRolesTableDisplay";

export type ScopeOptionsDto =
  components["schemas"]["Fixnetwork.SharedEntities.ScopedRoles.ScopeOptionsDto"];

function labelSort(a: SelectOption, b: SelectOption) {
  return a.label.localeCompare(b.label);
}

function buildReferenceMap(
  referenceScopedRoleSummaries: ScopeRoleSummaryDto[] | undefined
) {
  const idMap = new Map<string, string>();
  referenceScopedRoleSummaries?.forEach((option) => {
    if (option.brandId && option.brandName) {
      idMap.set(option.brandId, option.brandName);
    }
    if (option.countryId && option.countryName) {
      idMap.set(option.countryId, option.countryName);
    }
    if (option.provinceStateId && option.provinceStateName) {
      idMap.set(option.provinceStateId, option.provinceStateName);
    }
    if (option.shopId && option.shopName) {
      idMap.set(option.shopId, option.shopName);
    }
    if (option.roleId && option.roleName) {
      idMap.set(option.roleId, option.roleName);
    }
  });
  return idMap;
}

export function scopedRoleEntryFormatter(
  entry: ScopedRoleEntry,
  options: ScopeOptionsDto | undefined,
  referenceScopedRoleSummaries: ScopeRoleSummaryDto[] | undefined
): ScopedRoleTableRow {
  const idMap = buildReferenceMap(referenceScopedRoleSummaries);
  const role =
    entry.type === "Corporate"
      ? options?.corporateRoles?.find((r) => r.id === entry.roleId)
      : options?.shopRoles?.find((r) => r.id === entry.roleId);
  const brand = options?.brands?.find((b) => b.id === entry.brandId);
  const country = options?.countries?.find((c) => c.id === entry.countryId);
  const shop = options?.shops?.find((s) => s.id === entry.shopId);
  const provinceState = options?.provinceStates?.find(
    (ps) => ps.id === entry.provinceStateId
  );
  function getFromMap(key: string | null) {
    if (!key) {
      return null;
    }
    return idMap.get(key);
  }

  return {
    localId: entry.localId,
    role: formatValue(entry.roleId, [role?.name, getFromMap(entry.roleId)]),
    type: entry.type,
    brand: formatValue(entry.brandId, [
      brand?.displayName,
      brand?.twoLetterCode,
      getFromMap(entry.brandId),
    ]),
    country: formatValue(entry.countryId, [
      country?.displayName,
      country?.isoAlpha2Code,
      getFromMap(entry.countryId),
    ]),
    shop: formatValue(entry.shopId, [shop?.shopName, getFromMap(entry.shopId)]),
    provinceState: formatValue(entry.provinceStateId, [
      provinceState?.displayName,
      provinceState?.isoCode,
      getFromMap(entry.provinceStateId),
    ]),
  };
}

function formatValue(
  input: string | null,
  fallbackList: (string | undefined | null)[]
) {
  if (input === null) {
    return ALL_VALUE;
  }
  const fallback = fallbackList.find((x) => typeof x === "string");
  return fallback ?? input;
}

function defaultRowFormatter(
  entry: ScopedRoleEntry,
  options: ScopeOptionsDto | undefined
): ScopedRoleTableRow {
  const role =
    entry.type === "Corporate"
      ? options?.corporateRoles?.find((r) => r.id === entry.roleId)
      : options?.shopRoles?.find((r) => r.id === entry.roleId);
  const brand = options?.brands?.find((b) => b.id === entry.brandId);
  const country = options?.countries?.find((c) => c.id === entry.countryId);
  const shop = options?.shops?.find((s) => s.id === entry.shopId);
  const provinceState = options?.provinceStates?.find(
    (ps) => ps.id === entry.provinceStateId
  );

  return {
    localId: entry.localId,
    role: formatValue(entry.roleId, [role?.name]),
    type: entry.type,
    brand: formatValue(entry.brandId, [
      brand?.displayName,
      brand?.twoLetterCode,
    ]),
    country: formatValue(entry.countryId, [
      country?.displayName,
      country?.isoAlpha2Code,
    ]),
    shop: formatValue(entry.shopId, [shop?.shopName]),
    provinceState: formatValue(entry.provinceStateId, [
      provinceState?.displayName,
      provinceState?.isoCode,
    ]),
  };
}

export type PotentialScope = {
  type: "Corporate" | "Shop";
  roleId?: string | null;
  brandId?: string | null;
  countryId?: string | null;
  provinceStateId?: string | null;
  shopId?: string | null;
};

export function serializePotentialScope(entry: PotentialScope): string {
  return [
    entry.type,
    entry.roleId,
    entry.brandId,
    entry.countryId,
    entry.provinceStateId,
    entry.shopId,
  ]
    .map((el) => el || "*")
    .join("|");
}
export function deserializePotentialScope(key: string): PotentialScope {
  const parts = key.split("|");
  return {
    type: parts[0] === "Corporate" ? "Corporate" : "Shop",
    roleId: parts[1] === "*" ? null : parts[1],
    brandId: parts[2] === "*" ? null : parts[2],
    countryId: parts[3] === "*" ? null : parts[3],
    provinceStateId: parts[4] === "*" ? null : parts[4],
    shopId: parts[5] === "*" ? null : parts[5],
  };
}

export function fromPotentialScope(entry: PotentialScope): ScopedRoleEntry {
  return {
    localId: serializePotentialScope(entry),
    brandId: entry.brandId || null,
    countryId: entry.countryId || null,
    provinceStateId: entry.provinceStateId || null,
    roleId: entry.roleId || null,
    shopId: entry.shopId || null,
    type: entry.type,
  };
}

export function serializeSelects(selects: ScopedRoleSelect) {
  const potentialScope: PotentialScope = {
    type: selects.type,
    roleId:
      selects.type === "Corporate"
        ? selects.corporateRoleId
        : selects.shopRoleId,
    brandId: selects.brandId,
    countryId: selects.countryId,
    provinceStateId: selects.provinceStateId,
    shopId: selects.shopId,
  };
  return serializePotentialScope(potentialScope);
}

export function entryFromSelects(selects: ScopedRoleSelect): ScopedRoleEntry {
  const localId = serializeSelects(selects);
  return {
    localId,
    type: selects.type,
    brandId: selects.brandId,
    countryId: selects.countryId,
    shopId: selects.shopId,
    provinceStateId: selects.provinceStateId,
    roleId:
      selects.type === "Corporate"
        ? selects.corporateRoleId
        : selects.shopRoleId,
  };
}

export type ScopedRoleEntryFormatter = (
  entry: ScopedRoleEntry,
  options: ScopeOptionsDto | undefined
) => ScopedRoleTableRow;

export type UseScopedRolesProps<
  TFieldValues extends FieldValues,
  TScopesName extends FieldPath<TFieldValues>,
> = {
  formMethods: UseFormReturn<TFieldValues>;
  scopesFieldName: TScopesName;
  initialType?: "Shop" | "Corporate";
  required: boolean;
  dirtySelectionValidation?: boolean;
  data: ScopeOptionsDto | undefined;
  rowFormatter?: ScopedRoleEntryFormatter;
};

export function useScopedRolesForm<
  TFieldValues extends FieldValues,
  TScopesName extends FieldPath<TFieldValues>,
>({
  formMethods,
  initialType = "Corporate",
  scopesFieldName,
  required,
  dirtySelectionValidation = true,
  data,
  rowFormatter = defaultRowFormatter,
}: UseScopedRolesProps<TFieldValues, TScopesName>) {
  const { control, watch, getValues, setValue } = formMethods;
  const defaultValues = useMemo<ScopedRoleSelect>(() => {
    const _defaultValues: ScopedRoleSelect = {
      type: initialType,
      brandId: null,
      countryId: null,
      corporateRoleId: null,
      provinceStateId: null,
      shopRoleId: null,
      shopId: null,
    };
    return _defaultValues;
  }, [initialType]);
  const internalFormMethods = useForm<ScopedRoleSelect>({
    defaultValues,
  });
  const internalDirty = internalFormMethods.formState.isDirty;

  function setSelectsValue(
    field: Exclude<keyof ScopedRoleSelect, "type">,
    value: string | null
  ) {
    internalFormMethods.setValue(field, value);
  }

  function getScopesMap() {
    return getValues(scopesFieldName) as Map<string, ScopedRoleEntry>;
  }
  function setScopesMap(map: Map<string, ScopedRoleEntry>) {
    setValue(scopesFieldName, map as any);
  }

  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:ReactHookScopedRoles",
  });

  const notValidForBrandText = t("notValidForBrand", "N/A");
  const notValidForCountryText = t("notValidForCountry", "N/A");
  const {
    field: { value: scopedRolesMap, ref },
    fieldState: { error: scopedRolesError },
  } = useController({
    name: scopesFieldName,
    control,
    rules: {
      validate: {
        required: (value) => {
          if (!required) {
            return true;
          }
          return (
            value.size > 0 ||
            t("scopedRoleRequired", "At least one scope is required")
          );
        },
        dirtySelection: () => {
          if (dirtySelectionValidation && internalDirty) {
            return t(
              "scopedRoleDirtySelection",
              "Please add the current scope selection or clear the fields before submitting."
            );
          }
          return true;
        },
      },
    },
  });

  // Brand-Country mapping
  const brandCountryMap =
    data?.countryBrands?.reduce((acc, curr) => {
      if (!curr.brandId || !curr.countryId) {
        return acc;
      }
      const countries = acc.get(curr.brandId) || new Set<string>();
      countries.add(curr.countryId);
      acc.set(curr.brandId, countries);
      return acc;
    }, new Map<string, Set<string>>()) ?? new Map<string, Set<string>>();

  const countryBrandMap =
    data?.countryBrands?.reduce((acc, curr) => {
      if (!curr.brandId || !curr.countryId) {
        return acc;
      }
      const brands = acc.get(curr.countryId) || new Set<string>();
      brands.add(curr.brandId);
      acc.set(curr.countryId, brands);
      return acc;
    }, new Map<string, Set<string>>()) ?? new Map<string, Set<string>>();

  // Form operations
  function clearSelects() {
    internalFormMethods.reset(defaultValues);
    formMethods.clearErrors(scopesFieldName);
  }

  function removeScope(localId: string) {
    const map = getScopesMap();
    map.delete(localId);
    setScopesMap(map);
  }

  function clearScopes() {
    const map = getScopesMap();
    map.clear();
    setScopesMap(map);
  }

  // Computed values
  const selects = internalFormMethods.watch();
  const scopedRoleMap = watch(scopesFieldName) as Map<string, ScopedRoleEntry>;

  const brandOptions = (data?.brands ?? [])
    .reduce(
      (acc, curr) => {
        if (!curr.id || !curr.twoLetterCode) {
          return acc;
        }
        const label = curr.displayName;
        if (!label) {
          return acc;
        }
        const value = curr.id;
        if (!selects.countryId) {
          acc.push({ label, value, isValid: true });
          return acc;
        }
        const selectedCountryHasBrand = brandCountryMap
          .get(value)
          ?.has(selects.countryId);
        if (!selectedCountryHasBrand) {
          acc.push({
            label: `${label} (${notValidForCountryText})`,
            value,
            isValid: false,
          });
          return acc;
        }
        acc.push({ label, value, isValid: true });
        return acc;
      },
      [] as (SelectOption & { isValid: boolean })[]
    )
    .sort((a, b) => {
      if (a.isValid === b.isValid) {
        return labelSort(a, b);
      }
      if (a.isValid) {
        return -1;
      }
      return 1;
    });

  const provinceStateOptions = (data?.provinceStates ?? [])
    .reduce(
      (acc, entry) => {
        if (!entry.id || !entry.isoCode) {
          return acc;
        }
        const country =
          entry.countryDisplayName ||
          entry.countryIsoAlpha2Code ||
          t("unknownCountry", "Unknown Country");
        const provinceName = entry.displayName ?? entry.isoCode;
        const label = `${country} - ${provinceName}`;
        const value = entry.id;
        if (!selects.countryId && !selects.brandId) {
          acc.push({ label, value, isValid: true });
          return acc;
        }
        if (selects.brandId && !selects.countryId && entry.countryId) {
          const brandCountries = brandCountryMap.get(selects.brandId);
          const isValid = brandCountries?.has(entry.countryId);
          acc.push({
            label: isValid ? label : `${label} (${notValidForBrandText})`,
            value,
            isValid: Boolean(isValid),
          });
          return acc;
        }

        if (selects.countryId && entry.countryId !== selects.countryId) {
          acc.push({
            label: `${label} (${notValidForCountryText})`,
            value,
            isValid: false,
          });
          return acc;
        }
        acc.push({ label, value, isValid: true });
        return acc;
      },
      [] as (SelectOption & { isValid: boolean })[]
    )
    .sort((a, b) => {
      if (a.isValid === b.isValid) {
        return labelSort(a, b);
      }
      if (a.isValid) {
        return -1;
      }
      return 1;
    });

  const countryOptions = (data?.countries ?? [])
    .reduce(
      (acc, curr) => {
        if (!curr.id || !curr.displayName || !curr.isoAlpha2Code) {
          return acc;
        }
        const label = curr.displayName;
        if (!label) {
          return acc;
        }
        const value = curr.id;
        if (!selects.brandId) {
          acc.push({ label, value, isValid: true });
          return acc;
        }
        const selectedBrandHasCountry = countryBrandMap
          .get(value)
          ?.has(selects.brandId);
        if (!selectedBrandHasCountry) {
          acc.push({
            label: `${label} (${notValidForBrandText})`,
            value,
            isValid: false,
          });
          return acc;
        }
        acc.push({ label, value, isValid: true });
        return acc;
      },
      [] as (SelectOption & { isValid: boolean })[]
    )
    .sort((a, b) => {
      if (a.isValid === b.isValid) {
        return labelSort(a, b);
      }
      if (a.isValid) {
        return -1;
      }
      return 1;
    });

  const shopOptions = (data?.shops ?? [])
    .reduce(
      (acc, curr) => {
        if (!curr.shopName || !curr.id) {
          return acc;
        }
        const label = curr.shopName;
        const value = curr.id;
        if (selects.brandId && curr.brandId !== selects.brandId) {
          acc.push({
            label: `${label} (${notValidForBrandText})`,
            value,
            isValid: false,
          });
          return acc;
        }
        if (selects.countryId && curr.countryId !== selects.countryId) {
          acc.push({
            label: `${label} (${notValidForCountryText})`,
            value,
            isValid: false,
          });
          return acc;
        }
        if (
          selects.provinceStateId &&
          curr.provinceStateId !== selects.provinceStateId
        ) {
          return acc;
        }
        acc.push({
          label,
          value,
          isValid: true,
        });
        return acc;
      },
      [] as (SelectOption & { isValid: boolean })[]
    )
    .sort((a, b) => {
      if (a.isValid === b.isValid) {
        return labelSort(a, b);
      }
      if (a.isValid) {
        return -1;
      }
      return 1;
    });

  const corporateRoles =
    data?.corporateRoles
      ?.map((role) => ({
        label: role.name,
        value: role.id,
      }))
      .filter(isSelectOption)
      .sort(labelSort) ?? [];

  const shopRoles =
    data?.shopRoles
      ?.map((role) => ({
        label: role.name,
        value: role.id,
      }))
      .filter(isSelectOption)
      .sort(labelSort) ?? [];

  const scopedRoleDisplays = [...scopedRoleMap.values()].map((scopeEntry) =>
    rowFormatter(scopeEntry, data)
  );

  // Event handlers
  const handleBrandChange = (
    op: SelectOption<string | null> | null,
    reason?: AutocompleteChangeReason
  ) => {
    if (reason !== "selectOption") {
      return;
    }
    // check if valid country, else clear
    if (!op || !op.value) {
      return;
    }
    if (!selects.countryId) {
      return;
    }
    const selectedCountryHasBrand = brandCountryMap
      .get(op.value)
      ?.has(selects.countryId);
    if (selectedCountryHasBrand) {
      // valid selection, do nothing
      return;
    }
    // invalid selection, clear country
    setSelectsValue("countryId", null);
    setSelectsValue("provinceStateId", null);
    setSelectsValue("shopId", null);
    return;
  };

  const handleCountryChange = (
    op: SelectOption<string | null> | null,
    reason?: AutocompleteChangeReason
  ) => {
    if (reason === "clear") {
      setSelectsValue("provinceStateId", null);
    }
    if (reason !== "selectOption") {
      return;
    }
    if (!op || !op.value) {
      return;
    }
    // check if provinceState needs to be cleared
    if (selects.provinceStateId) {
      const provinceState = data?.provinceStates?.find(
        (ps) => ps.id === selects.provinceStateId
      );
      if (provinceState?.countryId !== op.value) {
        setSelectsValue("provinceStateId", null);
      }
    }
    // check if brand needs to be cleared
    if (selects.brandId) {
      const countryBrands = countryBrandMap.get(op.value);
      if (!countryBrands?.has(selects.brandId)) {
        setSelectsValue("brandId", null);
      }
    }
  };

  const handleProvinceStateChange = (
    op: SelectOption<string | null> | null,
    reason?: AutocompleteChangeReason
  ) => {
    if (reason !== "selectOption") {
      return;
    }
    if (!op || !op.value) {
      return;
    }
    const provinceState = data?.provinceStates?.find(
      (ps) => ps.id === op.value
    );
    setSelectsValue("countryId", provinceState?.countryId ?? null);
    // clear brand if not valid
    if (selects.brandId && provinceState?.countryId) {
      const brandCountries = brandCountryMap.get(selects.brandId);
      const isValid = brandCountries?.has(provinceState.countryId);
      if (!isValid) {
        setSelectsValue("brandId", null);
      }
    }
  };

  function expandSelects(selects: ScopedRoleSelect): ScopedRoleEntry[] {
    if (selects.brandId != null && selects.countryId != null) {
      return [entryFromSelects(selects)];
    }
    if (selects.brandId == null && selects.countryId != null) {
      // expand brands
      const brandsForCountry = countryBrandMap.get(selects.countryId);
      if (!brandsForCountry) {
        return [];
      }
      return [...brandsForCountry].map((brandId) => {
        return entryFromSelects({
          ...selects,
          brandId,
        });
      });
    }
    if (selects.brandId != null && selects.countryId == null) {
      // expand countries
      const countriesForBrand = brandCountryMap.get(selects.brandId);
      if (!countriesForBrand) {
        return [];
      }
      return [...countriesForBrand].map((countryId) => {
        return entryFromSelects({
          ...selects,
          countryId,
        });
      });
    }
    // expand both
    const entries: ScopedRoleEntry[] = [];
    brandCountryMap.forEach((brandCountryIds, mapBrandId) => {
      brandCountryIds.forEach((currentCountryId) => {
        entries.push(
          entryFromSelects({
            ...selects,
            brandId: mapBrandId,
            countryId: currentCountryId,
          })
        );
      });
    });
    return entries;
  }

  const handleShopChange = (
    op: SelectOption<string | null> | null,
    reason?: AutocompleteChangeReason
  ) => {
    if (reason !== "selectOption") {
      return;
    }
    if (!op || !op.value) {
      return;
    }
    const shop = data?.shops?.find((s) => s.id === op.value);
    if (!shop) {
      return;
    }
    setSelectsValue("brandId", shop.brandId ?? null);
    setSelectsValue("countryId", shop.countryId ?? null);
    setSelectsValue("provinceStateId", shop.provinceStateId ?? null);
    return;
  };

  const addScope = internalFormMethods.handleSubmit((selects) => {
    const entriesToAdd = expandSelects(selects);
    entriesToAdd.forEach((entry) => {
      scopedRolesMap.set(entry.localId, entry);
    });
    internalFormMethods.reset(defaultValues);
    formMethods.clearErrors(scopesFieldName);
  });

  return {
    // Data
    ref,
    selects,
    brandOptions,
    countryOptions,
    provinceStateOptions,
    shopOptions,
    corporateRoles,
    shopRoles,
    scopedRoleDisplays,
    scopedRolesError,
    scopesFieldName,

    // Functions
    clearSelects,
    addScopedRole: addScope,
    removeScopedRole: removeScope,
    clearScopes,
    handleBrandChange,
    handleCountryChange,
    handleShopChange,
    handleProvinceStateChange,
    // Form controls
    control,
    selectsControl: internalFormMethods.control,
  };
}
